import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'; // Importar useSearchParams
import { toast } from 'react-toastify';
import { resetPassword } from '../services/api';
import { Button } from "../components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Eye, EyeOff } from 'lucide-react'; // Importar ícones

function ResetPasswordPage() {
    const [searchParams] = useSearchParams(); // Usar useSearchParams
    const token = searchParams.get('token'); // Obter o token do parâmetro de consulta
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false); // Estado para visibilidade da nova senha
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); // Estado para visibilidade da confirmação de senha

    useEffect(() => {
        if (!token) {
            setError('Token de redefinição de senha inválido ou ausente na URL.');
            toast.error('Token de redefinição de senha inválido ou ausente.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem.');
            setIsLoading(false);
            return;
        }

        if (!token) {
            setError('Token de redefinição de senha inválido ou ausente.');
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(token, newPassword);
            toast.success('Sua senha foi redefinida com sucesso! Faça login com a nova senha.');
            navigate('/login');
        } catch (err) {
            console.error('Erro ao redefinir senha:', err);
            setError(err.message || 'Ocorreu um erro ao tentar redefinir sua senha.');
            toast.error(err.message || 'Erro ao redefinir senha.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-background">
            <Card className="w-full max-w-sm">
                <CardHeader className="text-center">
                    <img src="/ucm-logo.png" alt="Gestão de Presenças Logo" className="w-24 h-auto mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground mb-2">Universidade Católica de Moçambique</p>
                    <CardTitle className="text-2xl">Gestão de Presenças</CardTitle>
                    <CardDescription className="leading-relaxed"> {/* Adicionado leading-relaxed */}
                        Insira sua nova senha.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="grid gap-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
                        <div className="grid gap-2">
                            <Label htmlFor="newPassword" className="mb-1">Nova Senha</Label> {/* Adicionado mb-1 */}
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Sua nova senha"
                                    required
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={isLoading}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-primary transition-colors" // Adicionado transições
                                    disabled={isLoading}
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="confirmPassword" className="mb-1">Confirmar Nova Senha</Label> {/* Adicionado mb-1 */}
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirme sua nova senha"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={isLoading}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-primary transition-colors" // Adicionado transições
                                    disabled={isLoading}
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col">
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? 'A redefinir...' : 'Redefinir Senha'}
                        </Button>
                        <p className="mt-4 text-xs text-center text-muted-foreground">
                            <Link to="/login" className="underline transition-colors hover:text-primary"> {/* Adicionado transições */}
                                Voltar ao Login
                            </Link>
                        </p>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default ResetPasswordPage;